import { zodResolver } from '@hookform/resolvers/zod';
import { Box, Typography } from '@mui/material';
import { useCallback, useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { debounce } from 'src/utils/debounce';

import { InputField } from '../input-field';

import { useGoToPageInputValidationSchema } from './schema/go-to-page-input-validation-schema';

interface Props {
  totalPages: number;
  changePage: (newPage: number) => void;
  page: number | null;
}

export interface FormValues {
  page: number;
}

const defaultValues = {
  page: 1,
};

const GoToPageInput = ({ totalPages, changePage, page }: Props) => {
  const { t } = useTranslation(['common', 'validation']);

  const validationSchema = useGoToPageInputValidationSchema(totalPages);

  const {
    handleSubmit,
    control,
    reset,
    formState: { errors },
  } = useForm<FormValues>({
    defaultValues,
    mode: 'onChange',
    resolver: zodResolver(validationSchema),
  });

  const onSubmit = (data: FormValues) => {
    changePage(data.page);
  };

  const debouncedSubmit = useCallback(
    debounce(() => {
      handleSubmit(onSubmit)();
    }, 500),
    [],
  );

  useEffect(() => {
    if (page) {
      reset({ page });
    }
  }, [page]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography
          color="textSecondary"
          sx={{
            alignItems: 'center',
            display: 'flex',
          }}
          variant="body2"
          whiteSpace="nowrap">
          {t('common:goToPage')}
        </Typography>
        <Controller
          name="page"
          key={'page'}
          control={control}
          render={({ field: { onChange, value, onBlur, name }, fieldState: { error } }) => (
            <InputField
              sx={{
                width: 70,
              }}
              name={name}
              value={value}
              onBlur={onBlur}
              onChange={e => {
                onChange(e);
                debouncedSubmit();
              }}
              required
              error={!!error}
              type="number"
              min={1}
              max={totalPages}
            />
          )}
        />
      </Box>
      {errors?.page?.message ? (
        <Typography color="error" variant="caption">
          {errors.page.message}
        </Typography>
      ) : null}
    </Box>
  );
};

export default GoToPageInput;
